import { useEffect, useState } from 'react';
import { Link as RouterLink, NavLink, useLocation } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Drawer from '@mui/material/Drawer';
import Stack from '@mui/material/Stack';

import { brand, layout, shadows, LOGO_DIR } from '../theme';
import Img from './Img';
import { useCompany } from '../content/ContentProvider';

const NAV = [
	{ label: 'Home', to: '/' },
	{ label: 'Services', to: '/services' },
	{ label: 'Gallery', to: '/gallery' },
	{ label: 'About', to: '/about' },
	{ label: 'Contact', to: '/contact' },
];

/*
 * Fetches a page's chunk the moment a pointer settles on its link.
 *
 * The pages are split into separate files that normally download on click,
 * which puts the network wait between the click and the page. Hovering happens
 * a beat before clicking, so starting the download then means the chunk is
 * usually already here when the click lands. import() ignores repeat calls for
 * a module it has, so hovering the same link ten times costs one download.
 *
 * Keyed by path, matching NAV. The home page is not here because it is in the
 * main bundle and there is nothing to fetch.
 */
const PREFETCH = {
	'/services': () => import('../pages/Services'),
	'/gallery': () => import('../pages/Gallery'),
	'/about': () => import('../pages/About'),
	'/contact': () => import('../pages/Contact'),
	'/privacy-legal': () => import('../pages/PrivacyLegal'),
};

const prefetchRoute = (to) => PREFETCH[to]?.();

/**
 * A hamburger and a close cross, drawn rather than pulled from an icon font.
 *
 * @mui/icons-material is a 3MB dependency and this header needs two glyphs.
 */
function MenuGlyph({ open }) {
	return (
		<Box component="svg" viewBox="0 0 24 24" sx={{ width: 26, height: 26 }} aria-hidden="true">
			{open ? (
				<path
					d="M6 6l12 12M18 6L6 18"
					fill="none"
					stroke="currentColor"
					strokeWidth="2"
					strokeLinecap="round"
				/>
			) : (
				<path
					d="M4 7h16M4 12h16M4 17h16"
					fill="none"
					stroke="currentColor"
					strokeWidth="2"
					strokeLinecap="round"
				/>
			)}
		</Box>
	);
}

export default function Header() {
	const company = useCompany();
	const [scrolled, setScrolled] = useState(false);
	const [menuOpen, setMenuOpen] = useState(false);
	const { pathname } = useLocation();

	// The header sits over the hero, which is dark, so it starts transparent and
	// only takes on a background once the page has moved.
	useEffect(() => {
		const onScroll = () => setScrolled(window.scrollY > 8);

		onScroll();
		window.addEventListener('scroll', onScroll, { passive: true });

		return () => window.removeEventListener('scroll', onScroll);
	}, []);

	// Following a link should not leave the drawer hanging open behind the new page.
	useEffect(() => setMenuOpen(false), [pathname]);

	const onHome = pathname === '/';
	// The hero is bright, so the bar can sit transparent over it with the same
	// dark text it has everywhere else. Only the background and shadow change
	// on scroll — the text never has to flip colour.
	const transparent = onHome && !scrolled;
	const linkColour = brand.text;

	return (
		<>
			{/* The first thing a keyboard reaches, and invisible until it is focused. */}
			<Box
				component="a"
				href="#main"
				sx={{
					position: 'absolute',
					left: '-9999px',
					top: 0,
					zIndex: 1300,
					padding: '0.75rem 1.25rem',
					backgroundColor: brand.dark,
					color: brand.textInvert,
					'&:focus': { left: '0.5rem', top: '0.5rem' },
				}}
			>
				Skip to content
			</Box>

			<AppBar
				position="fixed"
				sx={{
					backgroundColor: transparent ? 'transparent' : brand.white,
					boxShadow: transparent ? 'none' : shadows.sm,
					borderBottom: transparent ? '1px solid transparent' : `1px solid ${brand.line}`,
					transition: 'background-color 300ms ease, box-shadow 300ms ease, border-color 300ms ease',
				}}
			>
				<Container>
					<Toolbar
						disableGutters
						sx={{
							// Phones keep the compact bar; from tablets up the bar is
							// taller and the logo gets air above and below.
							minHeight: { xs: layout.headerHeight, sm: layout.headerHeightWide },
							py: { xs: 0, sm: 1 },
							gap: 2,
						}}
					>
						<Box
							component={RouterLink}
							to="/"
							sx={{ display: 'flex', alignItems: 'center', gap: 1.75, textDecoration: 'none', mr: 'auto' }}
							aria-label={`${company.name} — home`}
						>
							{/*
								The same pair the PHP header shows: the house mark beside
								the wordmark, both from assets/img/logos/web/. Only the
								web/ files render at UI sizes — the ones at the theme root
								keep the artwork inside a mostly-empty 500x500 canvas and
								display as a speck.
							*/}
							<Img
								src={`${LOGO_DIR}/logo-con-trans.svg`}
								alt=""
								width={451}
								height={422}
								sx={{ height: '4.2rem', width: 'auto' }}
							/>
							<Img
								src={`${LOGO_DIR}/logo-txt-trans.svg`}
								alt=""
								width={455}
								height={130}
								// The wordmark carries no information the link's aria-label
								// does not, and on a phone it fights the hamburger for
								// room, so the mark stands alone there.
								sx={{ height: '3.15rem', width: 'auto', display: { xs: 'none', sm: 'block' } }}
							/>
						</Box>

						{/* Desktop navigation. */}
						<Stack
							component="nav"
							direction="row"
							spacing={0.5}
							sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center' }}
							aria-label="Primary"
						>
							{NAV.map((item) => (
								<Box
									key={item.to}
									component={NavLink}
									to={item.to}
									end={item.to === '/'}
									onMouseEnter={() => prefetchRoute(item.to)}
									onFocus={() => prefetchRoute(item.to)}
									sx={{
										position: 'relative',
										px: 1.5,
										py: 1,
										fontSize: '0.95rem',
										fontWeight: 600,
										textDecoration: 'none',
										color: linkColour,
										transition: 'color 200ms ease',
										'&:hover': { color: brand.accent },
										// The gold bar under the current page, which grows out
										// from the middle on hover.
										'&::after': {
											content: '""',
											position: 'absolute',
											left: '50%',
											bottom: '0.35rem',
											width: 0,
											height: '2px',
											backgroundColor: brand.accent,
											transform: 'translateX(-50%)',
											transition: 'width 250ms cubic-bezier(0.22, 1, 0.36, 1)',
										},
										'&:hover::after': { width: 'calc(100% - 1.5rem)' },
										'&.active': { color: brand.accent },
										'&.active::after': { width: 'calc(100% - 1.5rem)' },
									}}
								>
									{item.label}
								</Box>
							))}

							<Button
								variant="contained"
								color="primary"
								component={RouterLink}
								to="/contact"
								onMouseEnter={() => prefetchRoute('/contact')}
								onFocus={() => prefetchRoute('/contact')}
								sx={{ ml: 1.5 }}
							>
								Get a free estimate
							</Button>
						</Stack>

						{/* Mobile menu button. */}
						<IconButton
							onClick={() => setMenuOpen((open) => !open)}
							aria-expanded={menuOpen}
							aria-controls="mobile-nav"
							aria-label={menuOpen ? 'Close menu' : 'Open menu'}
							sx={{ display: { xs: 'inline-flex', lg: 'none' }, color: linkColour }}
						>
							<MenuGlyph open={menuOpen} />
						</IconButton>
					</Toolbar>
				</Container>
			</AppBar>

			<Drawer
				id="mobile-nav"
				anchor="right"
				open={menuOpen}
				onClose={() => setMenuOpen(false)}
				sx={{ display: { lg: 'none' } }}
				slotProps={{
					paper: {
						sx: { width: 'min(20rem, 82vw)', backgroundColor: brand.dark, color: brand.textInvert },
					},
				}}
			>
				<Stack sx={{ p: 3, pt: 10, gap: 0.5 }} component="nav" aria-label="Primary">
					{NAV.map((item) => (
						<Box
							key={item.to}
							component={NavLink}
							to={item.to}
							end={item.to === '/'}
							onTouchStart={() => prefetchRoute(item.to)}
							onFocus={() => prefetchRoute(item.to)}
							sx={{
								py: 1.5,
								fontSize: '1.15rem',
								fontWeight: 600,
								textDecoration: 'none',
								color: brand.textInvert,
								borderBottom: `1px solid ${brand.lineLight}`,
								'&.active': { color: brand.accent },
							}}
						>
							{item.label}
						</Box>
					))}

					<Button
						variant="contained"
						color="primary"
						component={RouterLink}
						to="/contact"
						sx={{ mt: 3 }}
						fullWidth
					>
						Get a free estimate
					</Button>

					<Box sx={{ mt: 3, fontSize: '0.95rem', color: brand.textInvertMuted }}>
						<Box component="a" href={company.phone_href} sx={{ color: brand.accent, fontWeight: 600 }}>
							{company.phone}
						</Box>
					</Box>
				</Stack>
			</Drawer>

			{/* The bar is fixed, so this keeps the page from starting underneath it.
			    The home page is exempt: its hero is meant to run up behind. */}
			{onHome ? null : (
				<Box sx={{ height: { xs: layout.headerHeight, sm: layout.headerHeightWide } }} />
			)}
		</>
	);
}
